(() => {
  class ScQuickOrder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-sc-quick-form]');
      if (!this.form) {
        this.dataset.ready = '';
        return;
      }

      this.search = this.querySelector('[data-sc-quick-search]');
      try {
        this.translations = JSON.parse(this.querySelector('[data-sc-quick-translations]')?.textContent || '{}');
      } catch (_) {
        this.translations = {};
      }
      try {
        this.giftConfig = JSON.parse(this.querySelector('[data-sc-quick-gift-config]')?.textContent || '{}');
      } catch (_) {
        this.giftConfig = {};
      }
      this.cartFeedback = this.querySelector('[data-sc-quick-cart-feedback]');
      this.cartError = this.querySelector('[data-sc-quick-cart-error]');
      this.cartLink = this.querySelector('[data-sc-quick-cart-link]');
      this.searchWrap = this.search?.closest('.sc-qo__search');
      this.categories = this.querySelector('[data-sc-quick-categories]');
      this.result = this.querySelector('[data-sc-quick-result]');
      this.empty = this.querySelector('[data-sc-quick-empty]');
      this.columns = this.querySelector('[data-sc-quick-columns]');
      this.itemConfigs = new WeakMap();
      this.itemElements = new WeakMap();
      this.items = [];
      this.selectedItems = new Set();
      this.groups = new Map();
      this.busy = false;
      this.cartReady = false;
      this.cartReadRevision = 0;
      this.cartSnapshot = { items: [] };
      this.cartQuantities = new Map();
      this.itemsByVariant = new Map();
      this.priceFactor = Number(this.dataset.priceFactor) || 1;

      this.totalQuantities = [...this.querySelectorAll('[data-sc-quick-total-quantity]')];
      this.subtotals = [...this.querySelectorAll('[data-sc-quick-subtotal]')];
      this.savings = [...this.querySelectorAll('[data-sc-quick-saving]')];
      this.goals = [...this.querySelectorAll('[data-sc-quick-goal]')];
      this.mobileProgress = [...this.querySelectorAll('[data-sc-quick-mobile-progress]')];
      this.submitButtons = [...this.querySelectorAll('[data-sc-quick-submit]')];

      this.onClick = this.handleClick.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onPageShow = this.handlePageShow.bind(this);
      this.onCartUpdate = this.handleCartUpdate.bind(this);
      this.onWindowFocus = () => this.loadCart(true);

      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.form.addEventListener('submit', this.onSubmit);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('sc:cart-updated', this.onCartUpdate);
      window.addEventListener('focus', this.onWindowFocus);

      this.indexRenderedItems();
      this.getItems().forEach((item) => {
        this.updateStepper(item);
        this.syncSelectedItem(item);
        this.updateCard(item);
      });
      this.updateFilter();
      try {
        const cart = JSON.parse(this.querySelector('[data-sc-quick-cart-state]')?.textContent || 'null');
        if (!Array.isArray(cart?.items)) throw new Error('Missing cart snapshot');
        this.hydrateCart(cart);
      } catch (_) {
        this.updateSummary();
        this.loadCart();
      }
      this.dataset.ready = '';
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      // Theme sections and drawers can move a live custom element synchronously.
      // Tear it down only when it actually leaves the document, then allow a
      // later insertion to initialize a fresh set of listeners and requests.
      queueMicrotask(() => {
        if (this.isConnected || !this.initialized) return;
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('input', this.onInput);
        this.removeEventListener('change', this.onChange);
        this.form?.removeEventListener('submit', this.onSubmit);
        window.removeEventListener('pageshow', this.onPageShow);
        window.removeEventListener('sc:cart-updated', this.onCartUpdate);
        window.removeEventListener('focus', this.onWindowFocus);
        this.cartReadRevision += 1;
        this.initialized = false;
      });
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    }

    t(key, variables = {}) {
      return Object.entries(variables).reduce(
        (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
        this.translations?.[key] || ''
      );
    }

    getItems() {
      return this.items || [...this.querySelectorAll('[data-sc-quick-item]')];
    }

    getItemElements(item) {
      if (this.itemElements.has(item)) return this.itemElements.get(item);
      const elements = {
        input: item.querySelector('[data-sc-quick-quantity]'),
        minus: item.querySelector('[data-sc-quick-minus]'),
        plus: item.querySelector('[data-sc-quick-plus]'),
        tiers: [...item.querySelectorAll('[data-sc-quick-tier]')],
        lineTotal: item.querySelector('[data-sc-quick-line-total]'),
        lineCompare: item.querySelector('[data-sc-quick-line-compare]'),
        lineCompareValue: item.querySelector('[data-sc-quick-line-compare-value]'),
        lineValue: item.querySelector('[data-sc-quick-line-value]'),
      };
      this.itemElements.set(item, elements);
      return elements;
    }

    getConfig(item) {
      if (this.itemConfigs.has(item)) return this.itemConfigs.get(item);
      let config = {};
      try {
        config = JSON.parse(item.querySelector('[data-sc-quick-item-config]')?.textContent || '{}');
      } catch (_) {
        config = {};
      }
      config.minimum = Math.max(1, Number(config.minimum) || 1);
      config.increment = Math.max(1, Number(config.increment) || 1);
      config.maximum = config.maximum == null ? null : Math.max(0, Number(config.maximum) || 0);
      config.price = Math.max(0, Number(config.price) || 0);
      config.compareAtPrice = Math.max(0, Number(config.compareAtPrice) || 0);
      config.tiers = Array.isArray(config.tiers)
        ? config.tiers
            .map((tier) => ({ quantity: Number(tier.quantity) || 0, price: Number(tier.price) || 0 }))
            .filter((tier) => tier.quantity > 0)
            .sort((a, b) => a.quantity - b.quantity)
        : [];
      this.itemConfigs.set(item, config);
      return config;
    }

    quantityFor(item) {
      const { input } = this.getItemElements(item);
      return Math.max(0, Math.floor(Number(input?.value) || 0));
    }

    syncSelectedItem(item) {
      if (this.quantityFor(item) > 0) this.selectedItems.add(item);
      else this.selectedItems.delete(item);
    }

    validQuantity(item, rawValue) {
      const config = this.getConfig(item);
      let value = Math.max(0, Math.floor(Number(rawValue) || 0));
      if (value === 0) return 0;
      value = Math.max(config.minimum, value);
      value = config.minimum + Math.ceil((value - config.minimum) / config.increment) * config.increment;
      if (config.maximum != null && value > config.maximum) {
        value = config.minimum + Math.floor((config.maximum - config.minimum) / config.increment) * config.increment;
        if (value < config.minimum) return 0;
      }
      return value;
    }

    setQuantity(item, value) {
      const { input } = this.getItemElements(item);
      if (!input || input.disabled) return;
      this.setVariantQuantity(String(this.getConfig(item).id), this.validQuantity(item, value));
      this.updateSummary();
    }

    setVariantQuantity(id, quantity) {
      (this.itemsByVariant.get(id) || []).forEach((item) => {
        const { input } = this.getItemElements(item);
        if (input) input.value = String(quantity);
        this.updateStepper(item);
        this.syncSelectedItem(item);
        this.updateCard(item);
      });
    }

    updateStepper(item) {
      const { input, minus, plus } = this.getItemElements(item);
      if (!input || input.disabled) return;
      const quantity = this.quantityFor(item);
      const maximum = input.dataset.max === undefined ? null : Math.max(0, Number(input.dataset.max) || 0);
      if (minus) minus.disabled = quantity === 0;
      if (plus) plus.disabled = maximum != null && quantity >= maximum;
    }

    unitPrice(config, quantity) {
      let price = config.price;
      for (const tier of config.tiers) {
        if (quantity < tier.quantity) break;
        price = tier.price;
      }
      return price;
    }

    updateCard(item) {
      const config = this.getConfig(item);
      const quantity = this.quantityFor(item);
      const unitPrice = this.unitPrice(config, quantity);
      const { tiers, lineTotal, lineCompare, lineCompareValue, lineValue } = this.getItemElements(item);

      let activeTier = null;
      tiers.forEach((tier) => {
        if (quantity >= Number(tier.dataset.min)) activeTier = tier;
      });
      tiers.forEach((tier) => {
        tier.setAttribute('aria-pressed', String(tier === activeTier));
      });

      const showCompare = config.compareAtPrice > (quantity ? unitPrice : config.price);
      if (lineCompare) lineCompare.hidden = !showCompare;
      if (lineCompareValue && showCompare) {
        lineCompareValue.textContent = this.formatMoney(config.compareAtPrice * (quantity || 1));
      }
      if (lineValue) lineValue.textContent = quantity ? this.formatMoney(unitPrice * quantity) : '';
      if (lineTotal) lineTotal.dataset.empty = String(quantity === 0);
    }

    totals() {
      const seen = new Set();
      return [...this.selectedItems].reduce(
        (totals, item) => {
          const id = String(this.getConfig(item).id);
          if (seen.has(id)) return totals;
          seen.add(id);
          const quantity = this.quantityFor(item);
          if (!quantity) return totals;
          const config = this.getConfig(item);
          const unitPrice = this.unitPrice(config, quantity);
          totals.positions += 1;
          totals.quantity += quantity;
          totals.subtotal += unitPrice * quantity;
          if (config.compareAtPrice > unitPrice) {
            totals.saving += (config.compareAtPrice - unitPrice) * quantity;
          }
          return totals;
        },
        { positions: 0, quantity: 0, subtotal: 0, saving: 0 }
      );
    }

    formatMoney(cents) {
      const amount = (Number(cents) || 0) / 100;
      const locale = this.dataset.locale || document.documentElement.lang || undefined;
      const currency = this.dataset.currency;
      try {
        return new Intl.NumberFormat(
          locale,
          currency
            ? { style: 'currency', currency }
            : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        ).format(amount);
      } catch (_) {
        return amount.toFixed(2);
      }
    }

    updateSummary() {
      const totals = this.totals();
      this.totalQuantities.forEach((element) => {
        element.textContent = String(totals.quantity);
      });
      this.subtotals.forEach((element) => {
        element.textContent = this.formatMoney(totals.subtotal);
      });
      this.savings.forEach((element) => {
        element.textContent = this.formatMoney(totals.saving);
      });

      const giftSubtotal = this.projectedGiftSubtotal();
      this.giftReached = Boolean(this.giftConfig.enabled && giftSubtotal >= Number(this.giftConfig.threshold));
      this.cartSubmission = window.ScQuickOrderCart?.buildSubmission(this.cartSnapshot, this.targetChanges(), {
        ...this.giftConfig, subtotal: giftSubtotal, eligibleVariantIds: [...this.itemsByVariant.keys()],
      });
      let firstGoalProgress = 0;
      this.goals.forEach((goal, index) => {
        const threshold = Math.max(0, Number(goal.dataset.threshold) || 0);
        const subtotal = goal.hasAttribute('data-sc-quick-gift-goal') ? giftSubtotal : totals.subtotal;
        const progress = threshold ? Math.min(1, subtotal / threshold) : 0;
        const reached = threshold > 0 && subtotal >= threshold;
        goal.dataset.reached = String(reached);
        const bar = goal.querySelector('[data-sc-quick-goal-progress]');
        const state = goal.querySelector('[data-sc-quick-goal-state]');
        if (bar) bar.style.width = `${Math.round(progress * 100)}%`;
        if (state) state.textContent = this.t(reached ? 'reached' : 'open');
        if (index === 0) firstGoalProgress = progress;
      });
      this.mobileProgress.forEach((element) => {
        element.style.width = `${Math.round(firstGoalProgress * 100)}%`;
      });

      this.submitButtons.forEach((button) => {
        button.disabled = this.busy || !this.cartReady || (totals.positions === 0 && this.targetChanges().length === 0);
      });
    }

    activeCategory() {
      return this.categories?.querySelector('[data-category][aria-pressed="true"]')?.dataset.category || '';
    }

    updateFilter() {
      const query = this.normalize(this.search?.value);
      const category = this.activeCategory();
      if (this.searchWrap) this.searchWrap.dataset.filled = String(Boolean(this.search?.value));
      let visible = 0;

      this.groups.forEach((group) => {
        let groupVisible = 0;
        group.querySelectorAll('[data-sc-quick-item]').forEach((item) => {
          const queryMatches = !query || this.normalize(item.dataset.search).includes(query);
          const categoryMatches = !category || item.dataset.group === category;
          const show = queryMatches && categoryMatches;
          item.hidden = !show;
          if (show) groupVisible += 1;
        });
        group.hidden = groupVisible === 0;
        const count = group.querySelector('[data-sc-quick-group-count]');
        if (count) count.textContent = this.t(groupVisible === 1 ? 'itemsOne' : 'itemsOther', { count: groupVisible });
        visible += groupVisible;
      });

      const total = this.getItems().length;
      if (this.columns) this.columns.hidden = visible === 0;
      if (this.empty) this.empty.hidden = visible !== 0;
      if (this.result) {
        this.result.textContent = query || category
          ? visible === 0
            ? this.t('noResults')
            : this.t('filteredCount', { visible, total })
          : this.result.hasAttribute('data-show-default-count')
            ? this.t(total === 1 ? 'itemsOne' : 'itemsOther', { count: total })
            : '';
      }
    }

    selectCategory(button) {
      this.categories?.querySelectorAll('[data-category]').forEach((chip) => {
        chip.setAttribute('aria-pressed', String(chip === button));
      });
      this.updateFilter();
    }

    resetFilter() {
      if (this.search) this.search.value = '';
      const all = this.categories?.querySelector('[data-category=""]');
      if (all) this.selectCategory(all);
      else this.updateFilter();
      this.search?.focus();
    }

    handleClick(event) {
      const clear = event.target.closest('[data-sc-quick-clear]');
      if (clear && this.contains(clear)) {
        this.resetFilter();
        return;
      }
      const reset = event.target.closest('[data-sc-quick-reset]');
      if (reset && this.contains(reset)) {
        this.resetFilter();
        return;
      }
      const chip = event.target.closest('[data-category]');
      if (chip && this.categories?.contains(chip)) {
        this.selectCategory(chip);
        return;
      }

      const tier = event.target.closest('[data-sc-quick-tier]');
      if (tier && this.contains(tier)) {
        const item = tier.closest('[data-sc-quick-item]');
        if (item && !tier.disabled) this.setQuantity(item, tier.dataset.min);
        return;
      }

      const minus = event.target.closest('[data-sc-quick-minus]');
      const plus = event.target.closest('[data-sc-quick-plus]');
      const button = minus || plus;
      if (!button || !this.contains(button)) return;
      const item = button.closest('[data-sc-quick-item]');
      if (!item) return;
      const current = this.quantityFor(item);
      const config = this.getConfig(item);
      if (plus) this.setQuantity(item, current === 0 ? config.minimum : current + config.increment);
      else this.setQuantity(item, current <= config.minimum ? 0 : current - config.increment);
    }

    handleInput(event) {
      if (event.target.matches('[data-sc-quick-search]')) {
        this.updateFilter();
        return;
      }
      if (event.target.matches('[data-sc-quick-quantity]')) {
        const item = event.target.closest('[data-sc-quick-item]');
        if (item) {
          this.setVariantQuantity(String(this.getConfig(item).id), this.quantityFor(item));
        }
        this.updateSummary();
      }
    }

    handleChange(event) {
      if (!event.target.matches('[data-sc-quick-quantity]')) return;
      const item = event.target.closest('[data-sc-quick-item]');
      if (item) this.setQuantity(item, event.target.value);
    }

    indexRenderedItems() {
      this.items = [];
      this.groups = new Map();
      this.querySelectorAll('[data-sc-quick-group]').forEach((group) => {
        const name = group.dataset.scQuickGroup || '';
        const items = [...group.querySelectorAll(':scope > [data-sc-quick-group-items] > [data-sc-quick-item]')];
        this.items.push(...items);
        if (!this.groups.has(name)) {
          this.groups.set(name, group);
        } else {
          const target = this.groups.get(name).querySelector('[data-sc-quick-group-items]');
          items.forEach((item) => target.appendChild(item));
          group.remove();
        }
      });
      this.itemsByVariant = new Map();
      this.items.forEach((item) => {
        const id = String(this.getConfig(item).id);
        if (!this.itemsByVariant.has(id)) this.itemsByVariant.set(id, []);
        this.itemsByVariant.get(id).push(item);
      });
      this.groups.forEach((group) => {
        const count = group.querySelector('[data-sc-quick-group-count]');
        if (count) {
          const total = group.querySelectorAll('[data-sc-quick-item]').length;
          count.textContent = this.t(total === 1 ? 'itemsOne' : 'itemsOther', { count: total });
        }
      });
    }

    targetChanges(normalize = false) {
      const targets = [];
      this.itemsByVariant.forEach((items, id) => {
        let quantity = this.quantityFor(items[0]);
        if (quantity === (this.cartQuantities.get(id) || 0)) return;
        if (normalize) {
          quantity = this.validQuantity(items[0], quantity);
          this.setVariantQuantity(id, quantity);
        }
        targets.push({ id, quantity });
      });
      return targets;
    }

    hydrateCart(cart, preserveEdits = false) {
      const drafts = preserveEdits ? this.targetChanges() : [];
      this.cartSnapshot = cart;
      this.cartQuantities = window.ScQuickOrderCart.quantities(cart);
      this.cartReady = true;
      this.selectedItems.clear();
      this.itemsByVariant.forEach((_, id) => this.setVariantQuantity(id, this.cartQuantities.get(id) || 0));
      drafts.forEach(({ id, quantity }) => this.setVariantQuantity(id, quantity));
      this.updateControls();
      this.updateSummary();
    }

    projectedGiftSubtotal() {
      const helper = window.ScQuickOrderCart;
      if (!helper) return 0;
      let subtotal = helper.eligibleSubtotal(this.cartSnapshot, this.priceFactor, [...this.itemsByVariant.keys()]);
      const groups = helper.linesByVariant(this.cartSnapshot);
      this.targetChanges().forEach(({ id, quantity }) => {
        const oldTotal = (groups.get(id) || []).reduce((sum, line) =>
          sum + Math.round(line.original_price * this.priceFactor) * line.quantity, 0
        );
        const config = this.getConfig(this.itemsByVariant.get(id)[0]);
        subtotal += this.unitPrice(config, quantity) * quantity - oldTotal;
      });
      return Math.max(0, subtotal);
    }

    updateControls() {
      const disabled = this.busy || !this.cartReady;
      this.getItems().forEach((item) => {
        const { input, minus, plus, tiers } = this.getItemElements(item);
        if (input) input.disabled = disabled;
        if (disabled) {
          if (minus) minus.disabled = true;
          if (plus) plus.disabled = true;
        } else this.updateStepper(item);
        const config = this.getConfig(item);
        tiers.forEach((tier) => {
          tier.disabled = disabled || (config.maximum != null && Number(tier.dataset.min) > config.maximum);
        });
      });
    }

    async loadCart(preserveEdits = false) {
      if (this.busy || !this.form) return;
      const revision = ++this.cartReadRevision;
      if (!this.cartReady) this.updateControls();
      try {
        const cart = await window.ScQuickOrderCart.request('cart.js');
        if (!this.isConnected || this.busy || revision !== this.cartReadRevision) return;
        this.hydrateCart(cart, preserveEdits);
      } catch (error) {
        if (revision !== this.cartReadRevision) return;
        this.showCartError('cartCheckError', error);
      }
    }

    handleCartUpdate(event) {
      if (this.busy) return;
      if (Array.isArray(event.detail?.cart?.items)) {
        this.cartReadRevision += 1;
        this.hydrateCart(event.detail.cart, true);
      } else this.loadCart(true);
    }

    showCartError(key, error, showLink = true) {
      if (this.cartFeedback) this.cartFeedback.hidden = false;
      if (this.cartError) {
        const description = error.message && error.message !== this.t(key) ? ` ${error.message}` : '';
        this.cartError.textContent = `${this.t(key)}${description}`;
      }
      if (this.cartLink) this.cartLink.hidden = !showLink;
    }

    setBusy(busy, refreshSummary = true) {
      this.busy = busy;
      this.updateControls();
      this.submitButtons.forEach((button) => {
        const label = button.querySelector('[data-sc-quick-button-label]');
        if (label) label.textContent = this.t(busy ? 'adding' : 'addToCart');
        const spinner = button.querySelector('.sc-quick-order-cart-spinner');
        if (spinner) spinner.hidden = !busy;
        if (busy) button.disabled = true;
      });
      if (!busy && refreshSummary) this.updateSummary();
    }

    async submitCart(body) {
      const helper = window.ScQuickOrderCart;
      const manager = window.ScQuickOrderGift;
      let responseReceived = false;
      let errorKey = 'cartCheckError';
      manager?.begin();
      this.cartReadRevision += 1;
      try {
        const cart = await helper.request('cart/update.js', body);
        responseReceived = true;
        this.hydrateCart(cart);
        manager?.notify(cart);
        const code = this.giftConfig.discountCode?.trim();
        const discount = code && cart.discount_codes?.find((item) => item.code.toLowerCase() === code.toLowerCase());
        if (discount?.applicable === false && helper.giftUnits(cart).size) {
          errorKey = 'discountError';
          throw new Error(this.t('discountError'));
        }
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        // A failed update can still change cart contents. Refresh only on failure;
        // a successful submission uses exactly one request for items, gift and code.
        if (!responseReceived) {
          try {
            const cart = await helper.request('cart.js');
            this.hydrateCart(cart);
            manager?.notify(cart);
          } catch (_) {
            this.cartReady = false;
          }
        }
        this.showCartError(errorKey, error);
        this.setBusy(false);
      } finally {
        manager?.end();
      }
    }

    handleSubmit(event) {
      event.preventDefault();
      if (this.busy || !this.cartReady) return;
      const targets = this.targetChanges(true);
      if (!targets.length && this.totals().positions === 0) {
        this.updateSummary();
        return;
      }
      this.updateSummary();
      const body = this.cartSubmission;
      if (this.cartFeedback) this.cartFeedback.hidden = true;
      this.setBusy(true);
      this.submitCart(body);
    }

    handlePageShow(event) {
      if (event.persisted || this.busy) {
        this.setBusy(false);
        this.loadCart();
      }
    }

  }

  if (!customElements.get('sc-quick-order')) {
    customElements.define('sc-quick-order', ScQuickOrder);
  }
})();
